import React, { useEffect, useMemo, useState } from "react"
import Modal from "react-modal"
import { useNavigate } from "react-router-dom"
import { SendGigModalProps } from "./model"
import { SelectBox } from "../custom"
import { Img } from "../custom/Img"
import { useGetGigsAvailableToinvite } from "../../hooks/gig-hooks"
import { JobbinPrimaryButton } from "../primaryButton"
import { JobbinSecondaryButton } from "../secondaryButton"
import { InvitationRequest } from "../../pages/candidate/InvitationRequest"
import * as applicationService from "../../../src/services/application-service"
import { useToast } from "../../context/toast"
import { useCredits } from "../../context/credits"

const INVITATION_EXPIRY_DAYS = 7

type Step = "form" | "confirm" | "success"

const SendGigModal = (props: SendGigModalProps) => {
  const navigate = useNavigate()
  const { notify } = useToast()
  const { pricing, balance, refresh } = useCredits()
  const getUserId = () => window.location.pathname.split("/")[2]

  const { gigs, isLoading: gigsLoading } = useGetGigsAvailableToinvite(getUserId())
  const [step, setStep] = useState<Step>("form")
  const [gigToInvite, setGigToInvite] = useState("")
  const [sending, setSending] = useState(false)

  // Resetare la fiecare deschidere, ca o invitație nouă să pornească curat.
  useEffect(() => {
    if (props.isOpen) {
      setStep("form")
      setGigToInvite("")
      setSending(false)
    }
  }, [props.isOpen])

  const cost = pricing?.invitationCost ?? 0
  const notEnoughCredits = balance != null && cost > 0 && balance < cost
  const balanceAfter = balance != null ? balance - cost : null

  const firstName = props.candidateName?.trim().split(/\s+/)[0]
  const titleWho = firstName ? `lui ${firstName}` : "candidatului"
  const summaryWho = props.candidateName?.trim() || "candidat"

  const gigsToUse = useMemo(
    () => gigs.map((gig) => ({ label: gig.gigTitle, value: gig.id })),
    [gigs]
  )
  const selectedGig = useMemo(
    () => gigs.find((g) => g.id === gigToInvite),
    [gigs, gigToInvite]
  )

  const goToCredits = () => {
    props.closeModal()
    navigate(`/credits?returnTo=${encodeURIComponent(window.location.pathname)}`)
  }

  const postJob = () => {
    props.closeModal()
    navigate("/post-job")
  }

  const finishSuccess = () => {
    props.closeModal()
    navigate("/candidates")
  }

  const doSendJob = async () => {
    if (!gigToInvite || sending) return
    setSending(true)
    const invitationRequest: InvitationRequest = {
      gigId: gigToInvite,
      candidateId: getUserId(),
    }

    try {
      const response = await applicationService.createInvitation(invitationRequest)

      if (response.ok) {
        refresh()
        setStep("success")
        return
      }

      let code = ""
      try {
        const body = await response.json()
        code = body?.["error-code"] ?? ""
      } catch {}

      if (response.status === 400 && code === "insufficient.credits") {
        notify("Nu ai destule credite pentru a trimite jobul. Cumpără un pachet și încearcă din nou.", "error")
        goToCredits()
      } else if (code === "application.already.exists") {
        notify("Ai trimis deja acest job acestui candidat. Alege altă poziție.", "error")
        setStep("form")
        setGigToInvite("")
      } else {
        notify("Eroare! Contactați suportul!", "error")
      }
    } catch {
      notify("Eroare! Contactați suportul!", "error")
    } finally {
      setSending(false)
    }
  }

  return (
    <Modal
      appElement={document.getElementById("root")!}
      isOpen={props.isOpen}
      onRequestClose={props.closeModal}
      shouldCloseOnOverlayClick
      className="mx-4 my-auto w-full max-w-[620px] rounded-[16px] bg-white outline-none"
      overlayClassName="fixed inset-0 z-50 flex items-center justify-center bg-gray-800/60 p-4"
    >
      <div className="rounded-[16px] bg-white p-5 sm:p-8">
        {/* ---------- PAS 1: formular ---------- */}
        {step === "form" && (
          <>
            <p className="text-center text-xl font-bold text-figmaBlack sm:text-2xl">
              Trimite un job {titleWho}
            </p>

            {gigsLoading ? (
              <div className="mt-6 animate-pulse space-y-3">
                <div className="h-12 rounded-xl bg-gray-100" />
                <div className="h-24 rounded-xl bg-gray-100" />
              </div>
            ) : gigsToUse.length === 0 ? (
              // PAS 5: empty state prietenos
              <div className="mt-6 rounded-2xl border border-gray-200 bg-figmaLightBg/60 px-5 py-8 text-center">
                <p className="text-base font-semibold text-figmaBlack">
                  Nu ai joburi active de trimis
                </p>
                <p className="mt-1 text-sm text-figmaDarkGray">
                  Postează un job, apoi îl poți trimite direct candidaților potriviți.
                </p>
                <div className="mx-auto mt-5 max-w-[260px]">
                  <JobbinPrimaryButton onClickAction={postJob} text="Postează un job" />
                </div>
              </div>
            ) : (
              <>
                <p className="mt-6 text-sm font-bold text-figmaDarkGray sm:mt-8 sm:text-base">
                  Alege poziția pentru care vrei să inviți candidatul
                </p>

                <div className="relative z-[60] mt-2">
                  <SelectBox
                    value={gigsToUse.find((o) => o.value === gigToInvite) ?? null}
                    onChange={(value) => setGigToInvite(String(value))}
                    className="w-full !text-left text-base leading-[normal] !text-gray-800"
                    placeholderClassName="!text-gray-800"
                    indicator={
                      <Img className="mr-0 h-6 w-6" src="/images/img_arrowdown.svg" alt="arrow_down" />
                    }
                    options={gigsToUse}
                    isMulti={false}
                    isSearchable={false}
                    placeholder="Selectează"
                    shape="square"
                    color="gray_800_64"
                    size="sm"
                    variant="outline"
                  />
                </div>

                {cost > 0 && (
                  <p className="mt-5 text-center text-sm text-figmaDarkGray">
                    Trimiterea costă <span className="font-bold text-figmaBlack">{cost} credite</span>
                    {balance != null && <span> · sold {balance}</span>}
                  </p>
                )}

                <div className="mt-4 sm:mt-6">
                  {notEnoughCredits ? (
                    <JobbinPrimaryButton
                      onClickAction={goToCredits}
                      text="Sold insuficient — cumpără credite"
                    />
                  ) : (
                    <JobbinPrimaryButton
                      onClickAction={() => setStep("confirm")}
                      text="Continuă"
                      disabled={!gigToInvite}
                    />
                  )}
                </div>
              </>
            )}
          </>
        )}

        {/* ---------- PAS 2: confirmare ---------- */}
        {step === "confirm" && (
          <>
            <p className="text-center text-xl font-bold text-figmaBlack sm:text-2xl">
              Confirmă trimiterea
            </p>

            <div className="mt-6 rounded-2xl border border-gray-200 p-5">
              <p className="text-sm text-figmaDarkGray">
                Trimiți poziția{" "}
                <span className="font-bold text-figmaBlack">{selectedGig?.gigTitle ?? "—"}</span> lui{" "}
                <span className="font-bold text-figmaBlack">{summaryWho}</span>.
              </p>

              {cost > 0 && (
                <div className="mt-4 flex items-center justify-between border-t border-gray-100 pt-4 text-sm">
                  <span className="text-figmaDarkGray">Cost</span>
                  <span className="font-bold text-figmaBlack">
                    {cost} credite
                    {balance != null && balanceAfter != null && (
                      <span className="font-normal text-figmaDarkGray">
                        {" "}
                        · sold {balance} → {balanceAfter}
                      </span>
                    )}
                  </span>
                </div>
              )}
            </div>

            <div className="mt-6 flex flex-col gap-3 sm:flex-row">
              <div className="w-full">
                <JobbinSecondaryButton onClickAction={() => setStep("form")} text="Înapoi" />
              </div>
              <div className="w-full">
                <JobbinPrimaryButton
                  onClickAction={doSendJob}
                  text={cost > 0 ? `Trimite — ${cost} credite` : "Trimite job"}
                  loading={sending}
                />
              </div>
            </div>
          </>
        )}

        {/* ---------- PAS 3: succes ---------- */}
        {step === "success" && (
          <div className="py-4 text-center">
            <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-green-100 text-2xl text-green-600">
              ✓
            </div>
            <p className="mt-4 text-xl font-bold text-figmaBlack">
              {firstName ? `${firstName} a fost notificat` : "Candidatul a fost notificat"}
            </p>
            <p className="mt-1 text-sm text-figmaDarkGray">
              Invitația expiră în {INVITATION_EXPIRY_DAYS} zile. Te anunțăm când răspunde.
            </p>
            <div className="mx-auto mt-6 max-w-[260px]">
              <JobbinPrimaryButton onClickAction={finishSuccess} text="Gata" />
            </div>
          </div>
        )}
      </div>
    </Modal>
  )
}

export default SendGigModal
